import {Injectable, Injector} from '@angular/core';
import {Log, LogTag} from '../Log';
import {PlayerBaseService} from "./player.base.service";
import {concatMap, mergeWith, Observable, of, Subject, switchMap, timeoutWith} from "rxjs";
import {delay, map, tap} from "rxjs/operators";
import {lastMap} from "../../rxjs/lastMap";
import {Watchdog} from "../Watchdog/Watchdog";
import {ServerSocket} from "./ServerSocket";
import {Stats} from "./stats";
import * as uuid from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class PlayerPwaService extends PlayerBaseService {
  deferredPrompt;
  first = true;
  timeoutId;
  broadcast;
  watchdog: Watchdog;
  instanceId;
  serverSocket: ServerSocket;
  statusInterval;
  count = 0;

  constructor(protected  injector: Injector) {
    super(injector);
    const logInfo = Log.getLogInfo("PlayerPwaService", "constructor", []);

    window.addEventListener('beforeinstallprompt', e => {
      Log.d(logInfo, LogTag.SERVICE_WORKER, 'beforeinstallprompt ' + e);
      this.deferredPrompt = e;
    });
    this.broadcast = new BroadcastChannel('signagePlayer');
    this.watchdog = new Watchdog(injector, this.extension, this.playerChannel$);

    this.playing$.subscribe( value => {
      if (this.serverSocket!=null) {
        this.serverSocket.sendStatus("playing", this.isPlaying() ? "1" : "0")
      }
    })

    this.playerChannel$.subscribe( value => {
      switch (value.cmd) {
        case 'watchdog':
          this.serverSocket.sendStatus("wdConnected", value.connected);
          this.serverSocket.sendStatus("wdExtension", value.extension);
          this.serverSocket.sendStatus("wdNative", value.native);
          break;
      }
    })
  }

  reload(): void {
    this.watchdog.reload();
  }

  get appType(): string {
    return this._appType;
  }

  set appType(value: string) {
    this._appType = value;
    this.watchdog.appType = value;
  }

  get extensionVersion(): string {
    return this.watchdog.extensionVersion;
  }

  runSingleInstances(): Observable<any> {
    const logInfo = Log.getLogInfo("PlayerPwaService", "runSingleInstances", []);
    return new Observable<any>(sub => {
      this.instanceId = String(new Date().getTime());
      this.broadcast.onmessage = (event) => {
        const instanceId  = event.data.instanceId;
        if (instanceId!=this.instanceId) {
          this.broadcast.postMessage({command: 'close', instanceId: instanceId});
        }
        if (event.data.command=='close') {
          window.close();
        }
      };
      Log.d(logInfo, LogTag.PLAYER_SERVICE, 'postMessage');
      this.broadcast.postMessage({command: 'new', instanceId: this.instanceId});
      sub.next(true);
      sub.complete();
    })
  }

  runningAsApp(): boolean {
    const logInfo = Log.getLogInfo("PlayerPwaService", "runningAsApp", []);
    if (this.isLocalHost) {
      return true;
    }
    const mqStandalone = '(display-mode: standalone)';
    const mqFullscreen = '(display-mode: fullscreen)';
    const r1 = (<any>navigator).standalone;
    const r2 = window.matchMedia(mqStandalone).matches;
    const r3 = window.matchMedia(mqFullscreen).matches;
    Log.d(logInfo, LogTag.SERVICE_WORKER, 'navigator.standalone=' + r1);
    Log.d(logInfo, LogTag.SERVICE_WORKER, 'display-mode: standalone)=' + r2);
    Log.d(logInfo, LogTag.SERVICE_WORKER, '(display-mode: fullscreen)=' + r3);
    if (r1 || r2 || r3) {
      Log.d(logInfo, LogTag.SERVICE_WORKER, 'running on installed App');
      return true;
    } else {
      Log.w(logInfo, LogTag.SERVICE_WORKER, 'running on browser');
      return false;
    }
  }

  downloadWin32() {
    const newWindow = window.open('https://galaxy.signage.me/deploy/signWatchdogSetup.exe');
  }

  downloadLinux() {
    const newWindow = window.open('https://galaxy.signage.me/deploy/linux_setup.tar.gz');
  }

  downloadAndroid() {
    const newWindow = window.open('https://galaxy.signage.me/deploy/android/signWatchdog.apk');
  }


  readyToInstall(): Observable<any> {
    if (this.isLocalHost) {
      return of(false);
    }
    return of(this.deferredPrompt!=null);
  }

  installPwa():Observable<any> {
    const logInfo = Log.getLogInfo("PlayerPwaService", "installPwa", []);
    Log.d(logInfo, LogTag.SERVICE_WORKER, '');
    return new Observable<any>( sub => {
      // Wait for the user to respond to the prompt
      if (this.deferredPrompt!=null) {
        this.deferredPrompt.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
            Log.d(logInfo, LogTag.SERVICE_WORKER, 'User accepted the A2HS prompt');
            sub.next(true);
            sub.complete();
          } else {
            Log.w(logInfo, LogTag.SERVICE_WORKER, 'Install canceled');
            window.close();
          }
          this.deferredPrompt = null;
        });
        this.deferredPrompt.prompt();
      } else {
        window.close();
      }
    })
  }


  init(): Observable<any> {
    const logInfo = Log.getLogInfo("PlayerPwaService", "init", []);
    return super.init().pipe(
      lastMap(r => this.runSingleInstances()),
      switchMap( r => {
        this.watchdog.start();

        const data = localStorage['registration'];
        if (data!=null) {
          const info = JSON.parse(data);
          this.domain = info.domain;
          this.businessId = info.businessId;
          this.stationId = info.stationId;
          this.stationToken = info.stationToken;
          Log.devMode = info.dev == '1';
          Log.d(logInfo, LogTag.INIT, 'domain=' + this.domain);
          Log.d(logInfo, LogTag.INIT, 'businessId=' + this.businessId);
          Log.d(logInfo, LogTag.INIT, 'stationId=' + this.stationId);
          Log.d(logInfo, LogTag.INIT, 'stationToken=' + this.stationToken);
          return this.caching.init(false,false).pipe(
              switchMap(r => this.updateCustomerInfo())
          )
        } else {
          return this.caching.init(false,false);
        }
      })
    )
  }

  generateToken() {
    this.stationToken = uuid.v4();
  }

  stationStats(): Observable<any>
  {
    return of('a').pipe(
      switchMap(a=> {
        this.stats = new Stats(this.businessDomain, this.businessId, this.stationId, this.stationToken);
        return this.stats.start();
      })
    )
  }


  saveStationInfo(): Observable<any> {
    const value: any = {};
    value.domain = this.domain;
    value.businessId = this.businessId;
    value.stationId = this.stationId;
    value.stationToken = this.stationToken;
    const info = JSON.stringify(value);
    localStorage['registration'] = info;


    if (localStorage['deployment']==null) {
      const deployment = {prompt: true};
      localStorage['deployment'] = JSON.stringify(deployment);
    }

    return of('save');
  }

  unregister() {
    delete localStorage['registration'];


    if (localStorage['deployment']!=null) {
      const deployment = JSON.parse(localStorage['deployment']);
      if (deployment.prompt!=null && deployment.prompt==false) {
        deployment.prompt = true;
        localStorage['deployment'] = JSON.stringify(deployment);
      }
    }


    window.close();
  }

  getSceneId(): Observable<any> {
    return this.caching.getRecord("stations", this.stationId).pipe(
      map( station => {
        const tags = station.log_tags.split(',');
        Log.updateConfig(station.log_enabled, station.log_priority, tags);
        return (station.changelist_id!=null) ? station.player_data_id : -1
      })
    )
  }


  onData(data: any): void {
    const logInfo = Log.getLogInfo("PlayerPwaService", "onData", [data]);
    Log.d(logInfo, LogTag.WEBSOCKET, '');
    switch (data.command) {
      case 'stop':
        this.stop();
        break;
      case 'start':
        this.play();
        break;
      case 'captureScreen':
        this.captureScreen(data.fileName, data.scale);
        break;
      case 'checkUpdates':
        break;
      case 'modifyRecords':
        this.caching.modifyRecords(data.table, data.ids);
        if (data.replay==true) {
          this.play();
        }
        break;
      case 'event':
        this.eventParams.dispatch(data.eventName, JSON.parse(data.eventParam), false);
        break;
      case 'configLog':
        Log.config = {
          untilTime: data.untilTime,
          maxPriority: data.maxPriority,
          debugTags: []
        }
        break;
      case 'rebootStation':
        this.reboot();
        break;
      case 'ping':
        break;
    }
  }


  captureScreen(fileName: string, scale: number) {
    this.watchdog.screenCapture(this.businessDomain, this.businessId, this.stationId, fileName, scale);
  }


  reboot() {
    this.watchdog.reboot();
  }



  startServerSocket() {
    const logInfo = Log.getLogInfo("PlayerPwaService", "startServerSocket", []);
    Log.d(logInfo, LogTag.SERVICE_WORKER, '');

    this.serverSocket = new ServerSocket(this);

    this.serverSocket.create(()=> {
      this.playerChannel$.next( {cmd: 'server', param: 'connected'} );


      this.serverSocket.sendStatus("startTime", this.startTime);

      this.serverSocket.sendStatus("appVersion", this.appVersion);
      this.serverSocket.sendStatus("libVersion", this.libVersion);

      this.serverSocket.sendStatus("platform", this.os);
      this.serverSocket.sendStatus("playing", this.isPlaying() ? "1" : "0");
      if (this.watchdog!=null) {
        this.serverSocket.sendStatus("wdConnected", this.watchdog.wdConnected);
        this.serverSocket.sendStatus("wdExtension", this.watchdog.wdExtension);
        this.serverSocket.sendStatus("wdNative", this.watchdog.wdNative);
      }

      // TEST ONLY ???
      if (this.businessId==315462) {
        this.serverSocket.sendPttInfo();
      }

    }, (args) => {
      Log.d(logInfo, LogTag.WEBSOCKET, 'onData: ' + JSON.stringify(args));
      this.onData(args)
    }, () => {
      Log.w(logInfo, LogTag.WEBSOCKET, 'ServerSocket was closed');
      if (this.statusInterval!=null) {
        clearInterval(this.statusInterval)
        this.statusInterval = null;
      }
      this.caching.setModifyAll();
      this.playerChannel$.next( {cmd: 'server', param: 'closed'} );
      this.timeoutId = setTimeout( () => {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
        this.startServerSocket();
      }, 10000)
    });
  }

  sendPttBlob(stations, emergency, data) {
    const url = "https://" + this.businessDomain + "/Web/ptt.ashx?command=blob&stationToken=" + this.stationToken + "&businessId=" + this.businessId + "&fromStationId=" + this.stationId+ "&stations=" + stations + "&emergency=" + emergency;
    const post = {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: data
    }
    fetch(url, post).then(response => {
      response.text().then(txt => {
      })
    }).catch(error => {
      Log.debug();
    })
  }

  setPtt(pttEnabled) {
    const url = "https://" + this.businessDomain + "/Web/ptt.ashx?command=set&stationToken=" + this.stationToken + "&businessId=" + this.businessId + "&fromStationId=" + this.stationId+ "&pttEnabled=" + pttEnabled;
    const post = {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: ""
    }
    fetch(url, post).then(response => {
      response.text().then(txt => {
      })
    }).catch(error => {
      Log.debug();
    })
  }


  dispose() {
     super.dispose();
  }
}
